import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';

const ImpersonationBanner = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check for impersonation cookie and get details
  const checkImpersonationStatus = () => {
    const impersonationCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('impersonation_token='));
    
    if (impersonationCookie) {
      setIsImpersonating(true);
      
      // Try to get shop info from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const shopId = urlParams.get('shopId');
      
      // For now, show basic info. In a real implementation, you might:
      // 1. Decode the JWT to get shop/user info
      // 2. Make an API call to get current impersonation details
      // 3. Store the info in localStorage/sessionStorage
      
      setImpersonationInfo({
        shopId: shopId,
        shopName: `Shop ${shopId}`, // Placeholder - would come from API/JWT
        userName: 'Shop Admin', // Placeholder - would come from API/JWT
        userRole: 'admin' // Placeholder - would come from API/JWT
      });
    } else {
      setIsImpersonating(false);
      setImpersonationInfo(null);
    }
  };

  // Exit impersonation
  const handleExitImpersonation = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.post('/superadmin/impersonate/exit');
      
      if (response.data.ok) {
        // Clear local state
        setIsImpersonating(false);
        setImpersonationInfo(null);
        
        // Redirect back to superadmin
        if (response.data.data?.redirect) {
          window.location.href = response.data.data.redirect;
        } else {
          window.location.href = '/superadmin';
        }
      }
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
      // Still try to redirect even if API call fails
      window.location.href = '/superadmin';
    } finally {
      setLoading(false);
    }
  };

  // Check status on mount and when cookies change
  useEffect(() => {
    checkImpersonationStatus();
    
    // Listen for cookie changes (simplified - in production you might use a more robust solution)
    const interval = setInterval(checkImpersonationStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  // Don't render if not impersonating
  if (!isImpersonating || !impersonationInfo) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-3 shadow-lg relative z-[90]" data-impersonation-banner style={{ position: 'fixed', top: '0', left: '0', right: '0' }}>
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-xl animate-pulse">🎭</span>
            <div>
              <div className="font-bold text-lg">
                Impersonating: {impersonationInfo.shopName}
              </div>
              <div className="text-sm opacity-90">
                Acting as: {impersonationInfo.userName} ({impersonationInfo.userRole})
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-sm opacity-90 hidden sm:block">
            All actions are being audited
          </div>
          <button
            onClick={handleExitImpersonation}
            disabled={loading}
            className="px-4 py-2 bg-black text-yellow-400 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-lg disabled:opacity-50"
          >
            {loading ? 'Exiting...' : 'Exit Impersonation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
